export interface FitScaleInput {
  /** Natural (untransformed) layout width of the element, px */
  naturalWidth: number;
  /** Natural (untransformed) layout height of the element, px */
  naturalHeight: number;
  /** Available width, px (viewport minus breathing room) */
  availableWidth: number;
  /** Available height, px (viewport minus breathing room) */
  availableHeight: number;
  /** Lower bound so content never shrinks below readability */
  minScale: number;
  /** Upper bound — allows upscaling past design size on roomy viewports. Optional; defaults to 1.6. */
  maxScale?: number;
}

/**
 *
 * Uniform fit-to-viewport scale factor for a fixed-geometry element.
 *
 * Returns the largest single scalar that fits the element within the available
 * box on both axes, clamped to [minScale, maxScale]. Unlike a hard cap-at-1 it
 * UPSCALES (up to maxScale) on roomy viewports and shrinks on tight ones, so the
 * frame tracks the viewport continuously in both directions while keeping its
 * aspect ratio (single scalar → undistorted art).
 *
 * Pure — no DOM. The caller supplies measured/queried dimensions.
 */
export function computeFitScale({
  naturalWidth,
  naturalHeight,
  availableWidth,
  availableHeight,
  minScale,
  maxScale = 1.6,
}: FitScaleInput): number {
  // Degenerate measurement (element not laid out yet) → no scaling.
  if (naturalWidth <= 0 || naturalHeight <= 0) return 1;

  const fit = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight);

  // Clamp to [minScale, maxScale]; maxScale > 1 permits upscaling.
  return Math.max(minScale, Math.min(maxScale, fit));
}
